import { decode } from 'jsonwebtoken'

import type { Organization, User } from '@/types/user.js'

/**
 * Routes that are public and don't require authentication
 */
export const publicRoutes = [
  '/sign-in',
  '/sign-up',
  '/discover',
  '/mcp/auth-complete',
]

/**
 * The route of the AI onboarding home page.
 */
export const onboardingHomeRoute = '/get-started'

/**
 * Routes that are public but still require to be authenticated
 * with an anonymous user to make API requests.
 */
export const anonymousRoutes = [onboardingHomeRoute]

export const authCookieName = 'auth_local'

/**
 * Returns the last active organization of the given user.
 *
 * If none can be found it returns the default organization.
 */
export function getLastActiveOrganization(
  user: Pick<User, 'memberships' | 'lastActiveOrganizationId'>,
) {
  const organizations = user.memberships.map((om) => om.organization)
  const organization = organizations.find(
    (o) => o._id === user.lastActiveOrganizationId,
  )

  return organization ?? defaultOrganization(organizations)
}

/**
 * Returns the default organization from the given list of organizations.
 *
 * If no default organization is found it throws.
 */
export function defaultOrganization(organizations: Organization[]) {
  const defaultOrg = organizations.find((o) => o.default)
  if (!defaultOrg) {
    throw new Error('Could not find default organization')
  }

  return defaultOrg
}

/**
 * Get's the `accountType` payload property from the passed JWT.
 *
 * @param token An access token from our auth endpoints.
 */
export function getAccountTypeFromToken(token: string | null) {
  if (!token) {
    return null
  }

  const jwtPayload = decode(token)
  if (
    typeof jwtPayload === 'object' &&
    jwtPayload !== null &&
    'accountType' in jwtPayload &&
    typeof jwtPayload.accountType === 'string'
  ) {
    return jwtPayload.accountType
  }

  return null
}
